import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCol,
  MDBIcon,
  MDBInput,
  MDBRow,
  MDBSpinner,
} from "mdb-react-ui-kit";
import {
  getMfmProcessesSummary,
  listMfmProcesses,
  getMfmProcessDetail,
  getMfmProcessEvents,
} from "Projects/CC_cubi_clan_project/Utils/MfmService";
import "../MFMHomeView/MFMHomeView.css";

function statusTone(status) {
  switch ((status || "").toLowerCase()) {
    case "success": return "success";
    case "running": return "warning";
    case "queued": return "secondary";
    case "retrying": return "info";
    case "failed": return "danger";
    default: return "light";
  }
}

function ProcessKpi({ title, value, tone = "secondary", icon = "chart-bar" }) {
  return (
    <MDBCard className={`mfm-kpi-card mfm-kpi-card--${tone} h-100`}>
      <MDBCardBody>
        <div className="mfm-kpi-head">
          <small>{title}</small>
          <MDBIcon fas icon={icon} />
        </div>
        <div className="mfm-kpi-value">{Number(value || 0)}</div>
      </MDBCardBody>
    </MDBCard>
  );
}

export default function MFMProcessesView({ selectedLibrary }) {
  const appCode = selectedLibrary?.appCode;
  const libraryId = selectedLibrary?.type === "library" ? selectedLibrary.idLibrary : undefined;

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processType, setProcessType] = useState("");
  const [status, setStatus] = useState("");
  const [fileId, setFileId] = useState("");
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [events, setEvents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sum, list] = await Promise.all([
        getMfmProcessesSummary({ appCode, libraryId, hours: 48 }),
        listMfmProcesses({
          appCode,
          libraryId,
          fileId: fileId ? Number(fileId) : undefined,
          processType: processType || undefined,
          status: status || undefined,
          page: 0,
          size: 100,
        }),
      ]);
      setSummary(sum || null);
      setRows(list || []);
    } catch (_) {
      setError("No se pudo cargar la monitorización de procesos.");
    } finally {
      setLoading(false);
    }
  }, [appCode, libraryId, processType, status, fileId]);

  useEffect(() => {
    loadData();
    const timer = window.setInterval(() => loadData(), 10000);
    return () => window.clearInterval(timer);
  }, [loadData]);

  const openDetail = useCallback(async (row) => {
    if (!row?.idProcess) return;
    setDetailLoading(true);
    try {
      const [detail, ev] = await Promise.all([
        getMfmProcessDetail(row.idProcess),
        getMfmProcessEvents(row.idProcess, { page: 0, size: 200 }),
      ]);
      setSelectedProcess(detail || row);
      setEvents(ev || []);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const counters = useMemo(() => ({
    queued: Number(summary?.queued || 0),
    running: Number(summary?.running || 0),
    retrying: Number(summary?.retrying || 0),
    failed: Number(summary?.failed || 0),
    success: Number(summary?.success || 0),
    total: Number(summary?.total || 0),
  }), [summary]);

  return (
    <div className="mfm-home p-4">
      <MDBRow className="mb-3 align-items-center">
        <MDBCol md="8">
          <h5 className="mb-0"><MDBIcon fas icon="list-check" className="me-2" />Revisión de procesos MFM</h5>
          <small className="text-muted">Estado, progreso, errores y trazas por tarea (transcode/miniaturas).</small>
        </MDBCol>
        <MDBCol md="4" className="text-md-end mt-2 mt-md-0">
          <MDBBtn size="sm" color="light" onClick={loadData}>
            <MDBIcon fas icon="arrows-rotate" className="me-1" />Actualizar
          </MDBBtn>
        </MDBCol>
      </MDBRow>

      <MDBRow className="g-3 mb-3">
        <MDBCol xs="6" md="4" xl="2"><ProcessKpi title="En cola" value={counters.queued} tone="neutral" icon="clock" /></MDBCol>
        <MDBCol xs="6" md="4" xl="2"><ProcessKpi title="Ejecutando" value={counters.running} tone="warning" icon="cogs" /></MDBCol>
        <MDBCol xs="6" md="4" xl="2"><ProcessKpi title="Reintento" value={counters.retrying} tone="info" icon="rotate" /></MDBCol>
        <MDBCol xs="6" md="4" xl="2"><ProcessKpi title="Fallidos" value={counters.failed} tone="danger" icon="triangle-exclamation" /></MDBCol>
        <MDBCol xs="6" md="4" xl="2"><ProcessKpi title="Completados" value={counters.success} tone="ok" icon="circle-check" /></MDBCol>
        <MDBCol xs="6" md="4" xl="2"><ProcessKpi title="Total" value={counters.total} tone="primary" icon="database" /></MDBCol>
      </MDBRow>

      <MDBCard className="mb-3">
        <MDBCardBody>
          <MDBRow className="g-2 align-items-end">
            <MDBCol md="3">
              <label className="form-label mb-1">Tipo</label>
              <select className="form-select" value={processType} onChange={(e) => setProcessType(e.target.value)}>
                <option value="">Todos</option>
                <option value="TRANSCODE">Transcode</option>
                <option value="THUMBNAIL">Miniatura</option>
                <option value="REPROCESS">Reproceso</option>
              </select>
            </MDBCol>
            <MDBCol md="3">
              <label className="form-label mb-1">Estado</label>
              <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Todos</option>
                <option value="queued">queued</option>
                <option value="running">running</option>
                <option value="retrying">retrying</option>
                <option value="failed">failed</option>
                <option value="success">success</option>
              </select>
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label="ID fichero"
                value={fileId}
                onChange={(e) => setFileId(e.target.value.replace(/[^0-9]/g, ""))}
              />
            </MDBCol>
            <MDBCol md="3" className="text-md-end">
              <MDBBtn size="sm" color="dark" onClick={loadData}>
                <MDBIcon fas icon="filter" className="me-1" />Filtrar
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBCardBody>
      </MDBCard>

      {loading && <div className="text-center py-4"><MDBSpinner className="me-2" />Cargando procesos...</div>}
      {error && (
        <MDBCard className="mfm-status-error-card mb-3">
          <MDBCardBody><MDBIcon fas icon="triangle-exclamation" className="me-2" />{error}</MDBCardBody>
        </MDBCard>
      )}

      {!loading && !error && (
        <MDBRow className="g-3">
          <MDBCol xl="8">
            <MDBCard>
              <MDBCardBody>
                <h6 className="mb-3">Listado de tareas</h6>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mfm-status-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Tipo</th>
                        <th>Estado</th>
                        <th>Progreso</th>
                        <th>Fichero</th>
                        <th>Creado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr><td colSpan="7" className="text-center text-muted py-3">Sin tareas para los filtros seleccionados.</td></tr>
                      )}
                      {rows.map((row) => (
                        <tr key={row.idProcess}>
                          <td>{row.idProcess}</td>
                          <td>{row.processType || "-"}</td>
                          <td><MDBBadge color={statusTone(row.status)} pill>{row.status || "-"}</MDBBadge></td>
                          <td style={{ minWidth: 160 }}>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress w-100" style={{ height: 8 }}>
                                <div className="progress-bar" style={{ width: `${Number(row.progressPct || 0)}%` }} />
                              </div>
                              <small>{Number(row.progressPct || 0)}%</small>
                            </div>
                          </td>
                          <td>
                            <div className="fw-semibold">#{row.fileId || "-"}</div>
                            <small className="text-muted">{row.originalName || row.uuid || "-"}</small>
                          </td>
                          <td><small>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}</small></td>
                          <td className="text-end">
                            <MDBBtn size="sm" color="light" onClick={() => openDetail(row)}>
                              <MDBIcon fas icon="magnifying-glass" className="me-1" />Detalle
                            </MDBBtn>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>

          <MDBCol xl="4">
            <MDBCard>
              <MDBCardBody>
                <h6 className="mb-3">Detalle y logs</h6>
                {detailLoading && <div className="text-center py-3"><MDBSpinner size="sm" className="me-2" />Cargando detalle...</div>}
                {!detailLoading && !selectedProcess && <div className="text-muted">Selecciona una tarea para ver su trazabilidad.</div>}
                {!detailLoading && selectedProcess && (
                  <>
                    <div className="mb-2"><strong>Proceso:</strong> #{selectedProcess.idProcess}</div>
                    <div className="mb-2"><strong>Estado:</strong> {selectedProcess.status || "-"}</div>
                    <div className="mb-2"><strong>Progreso:</strong> {Number(selectedProcess.progressPct || 0)}%</div>
                    <div className="mb-2"><strong>Worker:</strong> {selectedProcess.workerName || "-"}</div>
                    <div className="mb-3"><strong>Error:</strong> {selectedProcess.errorMessage || "-"}</div>

                    <div className="border rounded p-2" style={{ maxHeight: 420, overflowY: "auto" }}>
                      {events.length === 0 && <small className="text-muted">Sin eventos registrados.</small>}
                      {events.map((ev) => (
                        <div key={ev.idEvent} className="mb-2 pb-2 border-bottom">
                          <div className="d-flex justify-content-between gap-2">
                            <small className="fw-semibold">{ev.eventCode || "event"}</small>
                            <small className="text-muted">{ev.createdAt ? new Date(ev.createdAt).toLocaleTimeString() : "-"}</small>
                          </div>
                          <small className="d-block">{ev.message || "-"}</small>
                          <small className="text-muted">{ev.level || "INFO"} · {Number(ev.progressPct || 0)}%</small>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      )}
    </div>
  );
}
